import React from "react";
import { 
  TrendingUp, Users, ShieldCheck, Globe, 
  Target, Truck, Award, Briefcase, ArrowRight 
} from "lucide-react";

const AboutUs = () => {
  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* --- 1. HERO SECTION (Based on your Reference Image) --- */}
      <section className="relative h-[450px] flex items-center justify-center">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1600" 
            alt="Corporate Office" 
            className="w-full h-full object-cover"
          />
          {/* This creates the dark 'Business' look */}
          <div className="absolute inset-0 bg-[#0f172a]/80 mix-blend-multiply"></div> 
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-6 text-center text-white mt-12">
            <h4 className="text-[#008b7a] font-bold tracking-[0.3em] uppercase mb-4">Since 2015</h4>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Start your Business <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-200">
                in minutes.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10 font-light leading-relaxed">
              TradyFrog is India's most advanced B2B ecosystem. We bridge the gap between 
              local manufacturers and global markets through technology and trust.
            </p>
            
            {/* Search/Input Style (Like reference image) */}
            <div className="max-w-md mx-auto bg-white/10 backdrop-blur-md p-2 rounded-lg flex border border-white/20">
                <input 
                  type="email" 
                  placeholder="Enter your email to partner" 
                  className="bg-transparent text-white placeholder-gray-300 w-full px-4 outline-none"
                />
                <button className="bg-[#008b7a] hover:bg-[#007566] text-white px-6 py-3 rounded font-bold transition-all uppercase text-xs tracking-wider">
                  Join Now
                </button>
            </div>
        </div>
      </section>

      {/* --- 2. THE COMPANY OVERVIEW (Text Heavy) --- */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
           <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                 <div className="w-12 h-1 bg-[#008b7a]"></div>
                 <span className="text-sm font-bold uppercase text-gray-500 tracking-widest">Who We Are</span>
              </div>
              <h2 className="text-4xl font-bold text-[#1e293b] leading-tight">
                Architecting the future of <br/> Indian Trade.
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed text-justify">
                Established with a vision to organize the unorganized B2B sector, TradyFrog has evolved into a comprehensive digital marketplace. We are not just a platform; we are a technology-driven supply chain solution.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed text-justify">
                By integrating manufacturers, suppliers, and retailers onto a single unified dashboard, we eliminate the inefficiencies of traditional trading. Our infrastructure supports over 20,000 pin codes, ensuring that business has no boundaries.
              </p>
              
              <div className="pt-4 grid grid-cols-2 gap-6">
                  <div>
                      <h3 className="text-3xl font-bold text-[#008b7a]">100%</h3>
                      <p className="text-sm text-gray-500 font-semibold">Payment Protection</p>
                  </div>
                  <div>
                      <h3 className="text-3xl font-bold text-[#008b7a]">48h</h3>
                      <p className="text-sm text-gray-500 font-semibold">Dispatch Guarantee</p>
                  </div>
              </div>
           </div>
           
           <div className="relative h-[500px] bg-gray-100 rounded-lg overflow-hidden shadow-2xl">
              <img 
                 src="https://img.freepik.com/free-photo/business-colleagues-working-reports-communicating-while-working-office-focus-is-woman_637285-1061.jpg?semt=ais_user_personalization&w=740&q=80" 
                 alt="Business Meeting" 
                 className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 bg-white p-8 max-w-xs shadow-lg border-t-4 border-[#008b7a]">
                 <p className="italic text-gray-600 font-medium">
                    "We are building the digital backbone for India's MSMEs."
                 </p>
                 <p className="mt-4 font-bold text-[#1e293b]">- CEO, TradyFrog</p>
              </div>
           </div>
        </div>
      </section>

      {/* --- 3. INFRASTRUCTURE STATS (Dark Strip) --- */}
      <section className="bg-[#1e293b] text-white py-20">
         <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-gray-700">
               <div className="px-4">
                  <Globe className="mx-auto text-[#008b7a] mb-4 h-8 w-8" />
                  <h3 className="text-4xl font-bold">18+</h3>
                  <p className="text-gray-400 text-sm mt-2 uppercase tracking-wider">Countries Reach</p>
               </div>
               <div className="px-4">
                  <Users className="mx-auto text-[#008b7a] mb-4 h-8 w-8" />
                  <h3 className="text-4xl font-bold">50K+</h3>
                  <p className="text-gray-400 text-sm mt-2 uppercase tracking-wider">Happy Retailers</p>
               </div>
               <div className="px-4">
                  <Truck className="mx-auto text-[#008b7a] mb-4 h-8 w-8" />
                  <h3 className="text-4xl font-bold">1.2M</h3>
                  <p className="text-gray-400 text-sm mt-2 uppercase tracking-wider">Orders Shipped</p>
               </div>
               <div className="px-4">
                  <Briefcase className="mx-auto text-[#008b7a] mb-4 h-8 w-8" />
                  <h3 className="text-4xl font-bold">500+</h3>
                  <p className="text-gray-400 text-sm mt-2 uppercase tracking-wider">Employees</p>
               </div>
            </div>
         </div>
      </section>

      {/* --- 4. OUR MISSION & VISION (Card Layout) --- */}
      <section className="py-24 bg-gray-50">
         <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
               <h2 className="text-3xl font-bold text-[#1e293b]">Driven by Purpose</h2>
               <p className="text-gray-600 mt-4">Our core philosophy revolves around transparency, speed, and reliability.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
               {/* Card 1 */}
               <div className="bg-white p-10 shadow-sm border border-gray-200 hover:border-[#008b7a] transition-colors duration-300 group">
                  <div className="w-14 h-14 bg-teal-50 rounded-md flex items-center justify-center text-[#008b7a] mb-6 group-hover:bg-[#008b7a] group-hover:text-white transition-colors">
                     <Target size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1e293b] mb-4">Our Mission</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                     To democratize B2B commerce by providing small retailers access to the same resources and pricing as large conglomerates.
                  </p>
               </div>

               {/* Card 2 */}
               <div className="bg-white p-10 shadow-sm border border-gray-200 hover:border-[#008b7a] transition-colors duration-300 group">
                  <div className="w-14 h-14 bg-teal-50 rounded-md flex items-center justify-center text-[#008b7a] mb-6 group-hover:bg-[#008b7a] group-hover:text-white transition-colors">
                     <TrendingUp size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1e293b] mb-4">Our Vision</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                     To become India's most trusted digital supply chain partner, enabling a turnover of 1000Cr+ for our partner network by 2026.
                  </p>
               </div>

               {/* Card 3 */}
               <div className="bg-white p-10 shadow-sm border border-gray-200 hover:border-[#008b7a] transition-colors duration-300 group">
                  <div className="w-14 h-14 bg-teal-50 rounded-md flex items-center justify-center text-[#008b7a] mb-6 group-hover:bg-[#008b7a] group-hover:text-white transition-colors">
                     <ShieldCheck size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1e293b] mb-4">Core Values</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                     Integrity in pricing, transparency in logistics, and commitment to quality are the pillars upon which TradyFrog stands.
                  </p>
               </div>
            </div>
         </div>
      </section>

      {/* --- 5. DETAILED SERVICES (Lengthy content area) --- */}
      <section className="py-24 container mx-auto px-6">
         <div className="grid lg:grid-cols-2 gap-16">
            <div className="relative">
               <img 
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=1000" 
                  alt="Logistics" 
                  className="w-full h-full object-cover rounded-lg shadow-lg  transition-all duration-500"
               />
            </div>
            <div className="space-y-8">
               <h2 className="text-3xl font-bold text-[#1e293b]">End-to-End Supply Chain</h2>
               <div className="space-y-6">
                  <div className="flex gap-4">
                     <div className="mt-1">
                        <Award className="text-[#008b7a]" size={24} />
                     </div>
                     <div>
                        <h4 className="font-bold text-lg text-gray-900">Quality Assurance</h4>
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                           Every product on our platform goes through a rigorous 5-step quality check before it is listed, ensuring zero returns due to defects.
                        </p>
                     </div>
                  </div>
                  
                  <div className="w-full h-[1px] bg-gray-200"></div>

                  <div className="flex gap-4">
                     <div className="mt-1">
                        <Truck className="text-[#008b7a]" size={24} />
                     </div>
                     <div>
                        <h4 className="font-bold text-lg text-gray-900">Smart Logistics</h4>
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                           Our AI-powered logistics engine finds the fastest and cheapest route for your delivery, saving you up to 20% on shipping costs.
                        </p>
                     </div>
                  </div>

                  <div className="w-full h-[1px] bg-gray-200"></div>

                  <div className="flex gap-4">
                     <div className="mt-1">
                        <Briefcase className="text-[#008b7a]" size={24} />
                     </div>
                     <div>
                        <h4 className="font-bold text-lg text-gray-900">Credit Facility</h4>
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                           We understand business needs capital. TradyFrog offers easy credit lines to verified retailers to help them stock more and sell more.
                        </p>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* --- 6. CTA BANNER --- */}
      {/* <section className="bg-[#008b7a] py-16 text-center text-white">
         <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-4">Ready to scale your business?</h2>
            <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
               Join 50,000+ businesses who trust TradyFrog for their daily procurement needs.
            </p>
            <div className="flex justify-center gap-4">
               <button className="bg-white text-[#008b7a] px-8 py-3 rounded font-bold hover:bg-gray-100 transition-colors shadow-lg">
                  Register as Buyer
               </button>
               <button className="border-2 border-white text-white px-8 py-3 rounded font-bold hover:bg-white/10 transition-colors">
                  Become a Supplier
               </button>
            </div>
         </div>
      </section> */}

    </div>
  );
};

export default AboutUs;